import { EditIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { SelectFormField, TextFormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { FieldGroup } from '@/components/ui/field';
import { handleApiError } from '../lib/errors';
import {
  type AppRole,
  type AppRoleInput,
  type AppRoleValue,
  appRoleService,
} from '../services/appRoleService';
import { logger } from '../utils/logger';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'user', label: 'User' },
] as const;

interface FormData {
  user_name: string;
  role: AppRoleValue;
}

const emptyForm: FormData = {
  user_name: '',
  role: 'user',
};

function rowToForm(row: AppRole): FormData {
  return {
    user_name: row.user_name ?? '',
    role: row.role === 'admin' ? 'admin' : 'user',
  };
}

function formToPayload(form: FormData): AppRoleInput {
  return {
    user_name: form.user_name.trim().toLowerCase(),
    role: form.role,
  };
}

interface AppRoleDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (row: AppRole) => void;
  onEntrySaved?: () => void;
  row?: AppRole | null;
  isEdit?: boolean;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
}

export const AppRoleDrawer: React.FC<AppRoleDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  onEntrySaved,
  row,
  isEdit = false,
  readOnly = false,
  onEditRequest,
  onDeleteRequest,
}) => {
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if ((isEdit || readOnly) && row) {
        setFormData(rowToForm(row));
      } else {
        setFormData(emptyForm);
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen, isEdit, readOnly, row]);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }) as FormData);
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormData, string>> = {};
    const userName = formData.user_name.trim();
    if (!userName) {
      next.user_name = 'User name is required';
    } else if (userName.length > 100) {
      next.user_name = 'User name must be 100 characters or fewer';
    } else if (/\s/.test(userName)) {
      next.user_name = 'User name cannot contain spaces';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const payload = formToPayload(formData);
      const response =
        isEdit && row
          ? await appRoleService.update(row.id, payload)
          : await appRoleService.create(payload);
      onSave(response);
      onEntrySaved?.();
      onClose();
    } catch (error) {
      logger.error('Error saving app user:', error);
      setSubmitError(handleApiError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldsDisabled = readOnly;

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={readOnly ? 'View App User' : isEdit ? 'Edit App User' : 'Add App User'}
      description={
        readOnly
          ? 'App access details.'
          : isEdit
            ? 'Update this user’s access level.'
            : 'Grant a user access to the app.'
      }
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel={isEdit ? 'Update' : 'Add'}
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap justify-end gap-2 border-b border-border-default pb-4">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button type="button" size="sm" variant="destructive" onClick={onDeleteRequest}>
              <TrashIcon data-icon="inline-start" />
              Remove Access
            </Button>
          ) : null}
        </div>
      ) : null}
      <FieldGroup>
        <TextFormField
          label="User Name"
          value={formData.user_name}
          onChange={(v) => handleChange('user_name', v)}
          error={errors.user_name}
          required
          placeholder="e.g. jdoe"
          // The user name is the identity key matched against the sign-in
          // account, so it is fixed once the row exists.
          disabled={fieldsDisabled || isEdit}
          copyable={readOnly}
        />
        <SelectFormField
          label="Role"
          value={formData.role}
          onChange={(v) => handleChange('role', v)}
          options={[...ROLE_OPTIONS]}
          placeholder="Select role"
          required
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <p className="pt-2 text-sm text-text-secondary">
          Any user listed here can sign in. Only users with the <strong>Admin</strong> role can
          reach the settings pages.
        </p>
      </FieldGroup>
    </FormDetailPane>
  );
};
